import {
  useSimulateContract,
  useWriteContract,
  useWaitForTransactionReceipt,
  useAccount,
  useBalance,
} from 'wagmi';
import { parseEther, encodeFunctionData } from 'viem';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import BigNumber from 'bignumber.js';
import StakingABI from 'src/api/smart-contracts/abi/Staking';
import { CONTRACT_ADDRESSES } from 'src/constants';
import { trackSCWriteEvent } from 'src/utils';
import { useTwoTimestamps } from 'src/hooks/useTwoTimestamps';
import { useEstimateGas } from 'src/hooks/useEstimateGas';
import { TransactionLink } from 'src/components/TransactionLink/TransactionLink';

export function useMakeDeposit({
  amount,
  forceTargetAmount,
  onSuccess,
}: {
  amount: string;
  forceTargetAmount?: boolean;
  onSuccess?: (hash: string) => void;
}) {
  const queryClient = useQueryClient();
  const { address } = useAccount();
  const amountBN = BigNumber(amount).times(1e18);
  const targetAmount = parseEther(amount);

  const { timestamp1, timestamp2, setTimestamp2 } = useTwoTimestamps(amount);

  const {
    data: balance,
    isLoading: isLoadingBalance,
    refetch: refetchBalance,
  } = useBalance({
    address,
    query: {
      enabled: !!address,
    },
  });

  // for some reason on monad gasEstimate returns 0 with targetAmount equal to balance, so we are using 95% of targetAmount, since gas estimates are imprecise anyway
  const gasEstimateTargetAmount = BigInt(
    BigNumber(String(targetAmount)).times(0.95).toFixed(0)
  );

  const encodedData = address
    ? encodeFunctionData({
        abi: StakingABI,
        functionName: 'deposit',
        args: [gasEstimateTargetAmount, address],
      })
    : undefined;

  // Use explicit gas limit for deposit (recommended by Monad docs for known operations)
  // This avoids wallet estimation failures and provides better UX
  const {
    transactionCost,
    gasLimit,
    originalGasEstimate,
    isLoadingTransactionCost,
  } = useEstimateGas({
    encodedData,
    toAddress: CONTRACT_ADDRESSES.staking,
    value: gasEstimateTargetAmount,
    enabled: amountBN.gt(0),
    explicitGasLimit: 200_000n, // Base deposit gas limit
  });

  const balanceAmount = String(balance?.value || 0);
  const balanceMinusFees = BigNumber(balanceAmount).minus(transactionCost);
  const maxSafeBalance = balanceMinusFees.gte(0)
    ? balanceMinusFees
    : BigNumber(0);
  const amountEther = BigInt(
    forceTargetAmount
      ? amountBN.minus(transactionCost).toFixed(0)
      : !amountBN.gt(maxSafeBalance)
        ? amountBN.toFixed(0)
        : maxSafeBalance.toFixed(0)
  );
  const notEnoughBalanceForGas = transactionCost.gte(balanceAmount);

  const {
    data: config,
    isLoading: isSimulating,
    error: simulateError,
    refetch: refetchSimulate,
  } = useSimulateContract({
    abi: StakingABI,
    address: CONTRACT_ADDRESSES.staking,
    functionName: 'deposit',
    args: [amountEther, address!],
    value: amountEther,
    gas: gasLimit,
    query: {
      enabled: !!address && BigNumber(amountEther.toString()).gt(0),
    },
  });

  const {
    data: hash,
    isPending: isWriting,
    error: _writeError,
    writeContract,
  } = useWriteContract();

  const confirmationEnabled = !!hash;
  const writeError =
    timestamp1.current === timestamp2.current ? _writeError : undefined;

  const {
    isLoading: isConfirming,
    isSuccess,
    error: confirmError,
  } = useWaitForTransactionReceipt({
    hash,
    query: { enabled: confirmationEnabled },
  });

  useEffect(() => {
    if (isSuccess) {
      notifications.show({
        title: 'Deposit completed',
        message: (
          <div>
            <div>You successfully staked {amount} MON</div>
            {hash && <TransactionLink hash={hash} />}
          </div>
        ),
      });
      queryClient.refetchQueries();
      onSuccess?.(hash!);
      console.log('Deposit completed with config:', config);
      trackSCWriteEvent('deposit_completed', config, originalGasEstimate);
    }
  }, [isSuccess]);

  return {
    isLoadingBalance,
    isSimulating,
    balance,
    isDepositing: isWriting || isConfirming,
    depositError:
      simulateError ||
      writeError ||
      (confirmationEnabled ? confirmError : undefined),
    transactionCost: transactionCost.div(1e18),
    isLoadingTransactionCost,
    notEnoughBalanceForGas,
    makeDeposit: config?.request
      ? () => {
          writeContract(config.request);
          setTimestamp2();
        }
      : undefined,
    refetchBalance,
    refetchSimulate,
    writeContract,
  };
}
